import { SUPPORTED_LANGUAGES, SupportedLanguage } from "@/shared/i18n";
import i18next from "i18next";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
  useState,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";



interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  isChangingLanguage: boolean;
  supportedLanguages: readonly SupportedLanguage[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Memoize search params to prevent unnecessary re-renders
  const langParam = useMemo(() => searchParams.get("lang"), [searchParams]);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(
    i18next.language as SupportedLanguage
  );

  // Handle URL parameter language detection on mount
  useEffect(() => {
    const urlLang = langParam as SupportedLanguage;
    if (
      urlLang &&
      SUPPORTED_LANGUAGES.includes(urlLang) &&
      urlLang !== i18next.language
    ) {
      // Only change if different from current i18next language
      void i18next.changeLanguage(urlLang).then(() => {
        setCurrentLanguage(urlLang);
      });
    } else {
      // Sync with i18next's current language
      setCurrentLanguage(i18next.language as SupportedLanguage);
    }
  }, [langParam]); // Use memoized langParam instead of searchParams

  // Custom setLanguage that updates i18next, URL and localStorage
  const setLanguage = useCallback(async (language: SupportedLanguage) => {
    // Check against i18next directly to avoid dependency on currentLanguage
    if (language === i18next.language) return; // Avoid no-op
    
    setIsChangingLanguage(true);

    try {
      // Update i18next
      await i18next.changeLanguage(language);
      setCurrentLanguage(language);

      // Update URL parameter (replace to avoid polluting history)
      // Read current values inside callback to avoid dependencies
      const currentSearchParams = new URLSearchParams(window.location.search);
      const currentPathname = window.location.pathname;
      
      currentSearchParams.set("lang", language);
      navigate(
        { pathname: currentPathname, search: currentSearchParams.toString() },
        { replace: true }
      );

      // Update localStorage for persistence
      localStorage.setItem("preferred_language", language);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [navigate]); // Remove currentLanguage dependency

  const value = useMemo(() => {
    const contextValue = {
      currentLanguage,
      setLanguage,
      isChangingLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
    };
    
    if (import.meta.env.DEV) {
      console.log('[LanguageProvider] Context value recreated', {
        currentLanguage: contextValue.currentLanguage,
        isChangingLanguage: contextValue.isChangingLanguage
      });
    }
    
    return contextValue;
  }, [currentLanguage, setLanguage, isChangingLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
